import { Component, Input, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { LMBuilderEvent, LMBuilderModel } from "@components/builder/services/lm-builder-model.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector:    'lm-builder-total-by',
  standalone: false,
  templateUrl: './lm-builder-total-by.component.html',
  styleUrl: './lm-builder-total-by.component.scss',
})
export class LMBuilderTotalByComponent implements OnInit, OnDestroy{
  @Input() spec!: LMListPSpec;

  hasTotalField: WritableSignal<boolean> = signal(false);
  primarySetSpec: LMBuilderSubTotalByInfo | undefined;
  totalByInfoList: WritableSignal<Array<LMBuilderSubTotalByInfo>> = signal([]);

  private readonly destroyer$ = new Subject<void>();

  constructor(
    protected lmBuilderModel: LMBuilderModel
  ) {
    //no-op
  }

  ngOnInit(): void {
    this.lmBuilderModel.builderEvent$.pipe(
        takeUntil(this.destroyer$))
        .subscribe(event => {
          this.handleModelEvent(event);
        });

    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  private initialize() {
    this.initializeHasTotalField();
    this.initializePrimarySetSpec();
    this.initializeDisplayFieldSorts();
  }

  private initializeDisplayFieldSorts() {
    let hasDefault = false;
    // this.defaultTotalBy.set(undefined);
    this.totalByInfoList.set([]);

    if (this.spec.displayFields) {
      this.spec.displayFields.forEach(displayField => {
        if (displayField.type === 'field') {
          const specField = displayField.spec as LMFieldPSpec;
          const sortSpec = specField.sort;

          if (sortSpec) {
            if (sortSpec.type === 'set' && sortSpec.name.trim().length > 0) {
              if (sortSpec.default) {
                const defaultInfo = this.buildDisplayFieldSubtotalByInfo(displayField, sortSpec);
                if (defaultInfo) {
                  hasDefault = true;
                  defaultInfo.default = true;
                  this.totalByInfoList().unshift(defaultInfo);
                }
              } else {
                const secondaryInfo = this.buildDisplayFieldSubtotalByInfo(displayField, sortSpec);
                if (secondaryInfo) {
                  this.totalByInfoList().push(secondaryInfo);
                }
              }
            }
          }
        }
      });

      if (!hasDefault && this.primarySetSpec) {
        this.totalByInfoList().unshift(this.primarySetSpec);
      }
    }
  }

  private initializeHasTotalField() {
    this.hasTotalField.set(false);

    if (this.spec.summaryTotalFields.length > 0) {
      this.hasTotalField.set(true);
    } else if (this.spec.displayFields) {

      const totalDisplayField = this.spec.displayFields.find(displayField => {
        if (displayField.type === 'field') {
          const specField = displayField.spec as LMFieldPSpec;
          return (specField.total !== 'NONE');
        }
      });

      this.hasTotalField.set(totalDisplayField !== undefined);
    }
  }

  private initializePrimarySetSpec() {
    for (const key in this.spec.metaDataOptions.setSpecs) {
      if (this.spec.metaDataOptions.setSpecs[key].isPrimary) {
        const primaryInfo = this.buildImplicitSubtotalByInfo();
        if(primaryInfo) {
          this.primarySetSpec = primaryInfo;
        }
        break;
      }
    }
  }

  private handleModelEvent(event:LMBuilderEvent) {
    switch (event) {
      case LMBuilderEvent.COLUMN_TOTALS_CHANGED:
      case LMBuilderEvent.DISPLAY_FIELD_TOTAL_CHANGED:
      case LMBuilderEvent.DISPLAY_FIELD_REMOVED:
        this.initializeHasTotalField();
        this.initializeDisplayFieldSorts();
        break;
      case LMBuilderEvent.DISPLAY_FIELD_SORT_CHANGED:
        this.initializeDisplayFieldSorts();
        break;
      case LMBuilderEvent.SUMMARY_TOTALS_CHANGED:
        this.initializeHasTotalField();
        break;
    }
  }

  private buildImplicitSubtotalByInfo(): LMBuilderSubTotalByInfo | undefined {
    const primarySetSpec = this.findPrimarySetSpec();

    if (primarySetSpec) {
      const subTotalBy: Array<LMSubtotalOverride> = [];
      primarySetSpec.sortFields.forEach(sortField => {
        if (sortField.sortBreak) {
          subTotalBy.push(this.buildSubTotalBy(sortField.name, this.spec.subtotalBy));
        }
     });
      if (subTotalBy.length > 0) {
        return  {
          type: 'implicit',
          name: 'Primary',
          sortOrder: primarySetSpec.label,
          displayField: undefined,
          subTotalBy: subTotalBy,
          default: true,
          disable: subTotalBy.filter(value => value.enabled).length < 2,
        } as LMBuilderSubTotalByInfo;
      }
    }
    return undefined;
  }

  private buildDisplayFieldSubtotalByInfo(displayField: LMDisplayFieldPSpec, sortSpec: LMSortPSpec): LMBuilderSubTotalByInfo | undefined {
    const setSpec = this.findSetSpec(sortSpec.name);

    if (setSpec) {
      const subTotalBy: Array<LMSubTotalField> = [];

      setSpec.sortFields.forEach(sortField => {
        if (sortField.sortBreak) {
          subTotalBy.push(this.buildSubTotalBy(sortField.name, sortSpec.subtotalBy));
        }
      });

      let name = displayField.label.display.trim().length > 0 ? displayField.label.display : undefined;

      if (!name && displayField.type === 'field') {
        const fieldSpec = displayField.spec as LMFieldPSpec;
        name = fieldSpec.fieldId;
      }

      if (subTotalBy.length > 0) {
        return {
          type: 'displayField',
          name: name,
          sortOrder: setSpec.label,
          displayField: displayField,
          subTotalBy: subTotalBy,
          disable: subTotalBy.filter(value => value.enabled).length < 2,
        } as LMBuilderSubTotalByInfo;
      }
    }

    return undefined;
  }

  private buildSubTotalBy(sortField: string, subTotalFields:Array<LMSubTotalField> | undefined): LMSubTotalField {
    let info: LMSubTotalField | undefined;

    if (subTotalFields && subTotalFields?.length > 0) {
      info = subTotalFields.find(stb => {
        if (stb.field === sortField) {
          return true;
        }
      });

      return info ? info : { enabled: false, field: sortField, pageBreak: false };  // DPB this seems wrong!! should enable be true!!
    }

    return { enabled: true, field: sortField, pageBreak: false } as LMSubTotalField;
  }

  private findSetSpec(setName: string) : LMSetSpec | undefined {
    const sets = this.spec?.metaDataOptions?.setSpecs;
    if (sets && sets.hasOwnProperty(setName)) {
        return this.spec.metaDataOptions.setSpecs[setName];
    }

    return undefined;
  }

  private findPrimarySetSpec():  LMSetSpec | undefined {
    const sets = this.spec?.metaDataOptions?.setSpecs;

    if (sets) {
      const keys: Array<string> = Object.keys(sets);
      for (const key of keys) {
        if (sets[key].isPrimary) {
          return sets[key];
        }
      }
    }
    return undefined;
  }

  onColumnSubTotalByChanged(info: LMBuilderSubTotalByInfo) {
    const subTotalBy = info.subTotalBy;
    if (subTotalBy) {
      info.disable = subTotalBy.filter(override => override.enabled).length < 2;
    }

    if (info.type === 'implicit') {
      this.spec.subtotalBy = info.subTotalBy;
    } else {
      const displayField = info.displayField;
      if (displayField && displayField.type === 'field') {
        const fieldSpec = displayField.spec as LMFieldPSpec;

        if (fieldSpec.sort) {
          fieldSpec.sort.subtotalBy = info.subTotalBy;
        }
      }
    }
  }
}
